import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { verifyAdminSession, fetchDocumentSet, addDocument, updateDocument, deleteDocument } from '../services/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../hooks/useToast';

const CATEGORIES = [
  { value: 'cv', label: 'CV', icon: '📄', color: 'bg-blue-100 text-blue-800' },
  { value: 'certificat', label: 'Certificat de travail', icon: '📜', color: 'bg-green-100 text-green-800' },
  { value: 'diplome', label: 'Diplôme', icon: '🎓', color: 'bg-purple-100 text-purple-800' },
  { value: 'divers', label: 'Divers', icon: '📎', color: 'bg-amber-100 text-amber-800' },
];

const API_URL = import.meta.env.VITE_API_URL || '/cv/api';

export default function ManageDocuments() {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [documentSet, setDocumentSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  const emptyForm = {
    category: 'cv',
    category_label: 'CV',
    file_name: '',
    download_filename: '',
    sort_order: 0,
  };
  const [formData, setFormData] = useState(emptyForm);

  // Vérifier l'authentification
  useEffect(() => {
    verifyAdminSession().then(valid => {
      if (!valid) navigate('/admin/login');
    });
  }, [navigate]);

  // Charger l'ensemble et les fichiers disponibles
  useEffect(() => {
    loadData();
  }, [setId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger l'ensemble avec ses documents
      const set = await fetchDocumentSet(setId);
      setDocumentSet(set);

      // Charger les fichiers PDF disponibles sur le serveur
      try {
        const url = API_URL.endsWith('/api')
          ? `${API_URL}/files/list`
          : `${API_URL}/api/files/list`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.success && data.files) {
          setAvailableFiles(data.files);
        }
      } catch {
        setAvailableFiles([]);
      }
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (value) => CATEGORIES.find(c => c.value === value) || CATEGORIES[3];

  const handleCategoryChange = (categoryValue) => {
    const cat = getCategoryInfo(categoryValue);
    setFormData(prev => ({
      ...prev,
      category: categoryValue,
      category_label: cat.label,
    }));
  };

  const handleFileSelect = (fileName) => {
    const file = availableFiles.find(f => f.filename === fileName);
    setFormData(prev => ({
      ...prev,
      file_name: fileName,
      download_filename: prev.download_filename || file?.label?.replace(/\s+/g, '-') + '.pdf' || fileName,
    }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.file_name || !formData.category_label) return;

    try {
      setSaving(true);
      await addDocument({
        set_id: parseInt(setId),
        ...formData,
        sort_order: documentSet.documents ? documentSet.documents.length : 0,
      });
      setFormData(emptyForm);
      setShowAddForm(false);
      showToast('Document ajouté avec succès');
      await loadData();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (doc) => {
    setEditingId(doc.id);
    setFormData({
      category: doc.category,
      category_label: doc.category_label,
      file_name: doc.file_name,
      download_filename: doc.download_filename,
      sort_order: doc.sort_order,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateDocument(editingId, formData);
      setEditingId(null);
      setFormData(emptyForm);
      showToast('Document mis à jour avec succès');
      await loadData();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (doc) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer le document',
      message: `Voulez-vous supprimer le document "${doc.category_label}" ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteDocument(doc.id);
          showToast('Document supprimé');
          await loadData();
        } catch (err) {
          showToast(err.message, 'error');
        }
        setConfirmDialog({ isOpen: false, onConfirm: null });
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!documentSet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Ensemble non trouvé</p>
          <button onClick={() => navigate('/admin/document-sets')} className="text-primary-600 hover:underline">
            Retour aux ensembles
          </button>
        </div>
      </div>
    );
  }

  const documents = documentSet.documents || [];

  // Formulaire réutilisable inline (PAS un composant — évite le remount React)
  const renderDocumentForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      {/* Catégorie */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleCategoryChange(cat.value)}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                formData.category === cat.value
                  ? 'border-primary-500 bg-primary-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-xl block mb-1">{cat.icon}</span>
              <span className="text-xs font-medium text-gray-700">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Label personnalisé */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Label affiché dans l'onglet *
        </label>
        <input
          type="text"
          required
          value={formData.category_label}
          onChange={(e) => setFormData(prev => ({ ...prev, category_label: e.target.value }))}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Ex: Certificat Google Cloud"
        />
      </div>

      {/* Fichier PDF */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fichier PDF *</label>
        {availableFiles.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {availableFiles.map((file) => (
              <button
                key={file.filename}
                type="button"
                onClick={() => handleFileSelect(file.filename)}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  formData.file_name === file.filename
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{file.icon || '📄'}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.label || file.filename}</p>
                    <p className="text-xs text-gray-500">{file.sizeFormatted}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-3">
            Aucun fichier disponible.{' '}
            <button type="button" onClick={() => navigate('/admin/manage-files')} className="text-primary-600 hover:underline font-medium">
              Uploader un document
            </button>
          </p>
        )}
        {formData.file_name && (
          <p className="text-xs text-green-600 mt-1.5 flex items-center space-x-1">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Fichier : {formData.file_name}</span>
          </p>
        )}
      </div>

      {/* Nom de téléchargement */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Nom lors du téléchargement
        </label>
        <input
          type="text"
          value={formData.download_filename}
          onChange={(e) => setFormData(prev => ({ ...prev, download_filename: e.target.value }))}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="CV-Stephane-Dupont.pdf"
        />
        <p className="text-xs text-gray-400 mt-1">
          Nom du fichier que le recruteur recevra lors du téléchargement
        </p>
      </div>

      {/* Validation errors */}
      {!formData.file_name && formData.category_label && (
        <p className="text-xs text-amber-600 flex items-center space-x-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>Veuillez sélectionner un fichier PDF</span>
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center space-x-3 pt-2">
        <button
          type="submit"
          disabled={saving || !formData.file_name}
          className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors font-medium flex items-center space-x-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          <span>{submitLabel}</span>
        </button>
        <button
          type="button"
          onClick={() => { setShowAddForm(false); setEditingId(null); setFormData(emptyForm); }}
          className="px-5 py-2.5 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors"
        >
          Annuler
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
            <button onClick={() => navigate('/admin')} className="hover:text-primary-600 transition-colors">Admin</button>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <button onClick={() => navigate('/admin/document-sets')} className="hover:text-primary-600 transition-colors">Ensembles</button>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-900 font-medium truncate">{documentSet.label}</span>
          </nav>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{documentSet.label}</h1>
              {documentSet.description && (
                <p className="text-gray-500">{documentSet.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => { setShowAddForm(true); setEditingId(null); setFormData(emptyForm); }}
                className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Ajouter un document</span>
              </button>
              <button
                onClick={() => navigate('/admin/document-sets')}
                className="px-4 py-2.5 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Retour</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 bg-primary-50 border-b border-primary-100">
              <h2 className="text-lg font-semibold text-primary-900">Ajouter un document</h2>
            </div>
            {renderDocumentForm(handleAdd, 'Ajouter')}
          </div>
        )}

        {/* Liste des documents */}
        {documents.length === 0 && !showAddForm ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun document dans cet ensemble</h3>
            <p className="text-gray-500 mb-6">Ajoutez votre premier document pour compléter ce dossier.</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium inline-flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Ajouter un document</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Preview des onglets */}
            {documents.length > 0 && !showAddForm && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Aperçu des onglets recruteur</p>
                <div className="flex flex-wrap gap-1">
                  {documents.map((doc, i) => {
                    const cat = getCategoryInfo(doc.category);
                    return (
                      <span
                        key={doc.id}
                        className={`inline-flex items-center px-3 py-1.5 rounded-t-lg text-sm font-medium ${
                          i === 0 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span className="mr-1.5">{cat.icon}</span>
                        {doc.category_label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {documents.map((doc, index) => {
              const cat = getCategoryInfo(doc.category);

              return (
                <div
                  key={doc.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  {editingId === doc.id ? (
                    <div>
                      <div className="px-6 py-3 bg-amber-50 border-b border-amber-100">
                        <span className="text-sm font-medium text-amber-800">Modification du document</span>
                      </div>
                      {renderDocumentForm(handleUpdate, 'Enregistrer')}
                    </div>
                  ) : (
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        {/* Numéro d'ordre */}
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold text-gray-500">
                          {index + 1}
                        </div>

                        {/* Icône catégorie */}
                        <span className="text-2xl flex-shrink-0">{cat.icon}</span>

                        {/* Infos */}
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2 mb-0.5">
                            <h3 className="font-semibold text-gray-900 truncate">{doc.category_label}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cat.color}`}>
                              {cat.label}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            <span className="flex items-center space-x-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span>{doc.file_name}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span>{doc.download_filename}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 ml-4">
                        <button
                          onClick={() => handleEditStart(doc)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
      />
    </div>
  );
}
